import { CartUpdateEvent } from '@theme/events';

const CAREGIVER_SWITCH_FLAG = 'caregiver_switch_clear_cart';
const CART_SYNC_SOURCE_ID = 'cart-sync';
const EXTERNAL_CART_EVENTS = ['cart:updated', 'cart:refresh', 'cart:change'];

/** @type {boolean} */
let caregiverSwitchSyncInFlight = false;

/**
 * Syncs cart drawer empty-state classes after the server cart count changes.
 * @param {number} itemCount
 */
function updateCartDrawerEmptyState(itemCount) {
  const dialog = document.querySelector('.cart-drawer__dialog');
  if (!dialog) return;

  if (itemCount === 0) {
    dialog.classList.add('cart-drawer--empty');
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading-empty');
  } else {
    dialog.classList.remove('cart-drawer--empty');
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');
  }
}

/**
 * Updates visible cart counts that are not wired through cart-icon (e.g. patient panel).
 * @param {number} itemCount
 */
function updateCartCountElements(itemCount) {
  const displayCount = itemCount < 100 ? String(itemCount) : '99+';

  sessionStorage.setItem(
    'cart-count',
    JSON.stringify({
      value: String(itemCount),
      timestamp: Date.now(),
    })
  );

  document.querySelectorAll('[data-cart-count]').forEach((element) => {
    element.textContent = displayCount;
  });

  document.querySelectorAll('cart-icon').forEach((icon) => {
    if ('renderCartBubble' in icon && typeof icon.renderCartBubble === 'function') {
      icon.renderCartBubble(itemCount, false, false);
    }
  });

  document.querySelectorAll('.cart-drawer__heading .cart-bubble__text-count').forEach((element) => {
    element.textContent = itemCount < 100 ? String(itemCount) : '';
    element.classList.toggle('hidden', itemCount === 0);
    element.closest('.cart-bubble')?.classList.toggle('visually-hidden', itemCount === 0);
  });
}

/**
 * Closes the cart drawer when a background sync runs (e.g. caregiver profile switch).
 */
function closeCartDrawerIfOpen() {
  const dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('.cart-drawer__dialog'));
  const drawer = /** @type {{ close?: () => void } | null} */ (document.querySelector('cart-drawer-component'));

  if (!dialog?.open || !drawer?.close) {
    return;
  }

  drawer.close();
}

/**
 * Updates cart icon, drawer, and line items using the theme's cart update pipeline.
 * @param {{ item_count?: number }} cart - Cart object from /cart.js
 */
function updateCartUI(cart) {
  const itemCount = cart?.item_count ?? 0;

  updateCartCountElements(itemCount);
  updateCartDrawerEmptyState(itemCount);

  document.dispatchEvent(
    new CartUpdateEvent(cart, CART_SYNC_SOURCE_ID, {
      itemCount,
      source: 'caregiver-switch',
    })
  );

  closeCartDrawerIfOpen();
}

/**
 * Fetches the current cart from the server and refreshes all cart UI.
 * @returns {Promise<Object>}
 */
async function refreshCartFromServer() {
  const response = await fetch('/cart.js');

  if (!response.ok) {
    throw new Error('Failed to fetch cart');
  }

  const cart = await response.json();
  updateCartUI(cart);
  return cart;
}

/**
 * @param {Event} event
 */
function handleExternalCartEvent(event) {
  const cart = /** @type {CustomEvent} */ (event).detail?.cart;

  if (cart) {
    updateCartUI(cart);
    return;
  }

  refreshCartFromServer().catch((error) => {
    console.error('[cart-sync] Failed to refresh cart', error);
  });
}

async function handleCaregiverSwitchLanding() {
  if (caregiverSwitchSyncInFlight) return;

  caregiverSwitchSyncInFlight = true;

  try {
    const hadFlag = sessionStorage.getItem(CAREGIVER_SWITCH_FLAG);

    if (hadFlag) {
      sessionStorage.removeItem(CAREGIVER_SWITCH_FLAG);

      try {
        await fetch('/cart/clear.js', { method: 'POST' });
      } catch (error) {
        console.error('[cart-sync] Failed to clear cart after caregiver switch', error);
      }
    }

    await refreshCartFromServer();
  } finally {
    caregiverSwitchSyncInFlight = false;
  }
}

function initCartSync() {
  for (const name of EXTERNAL_CART_EVENTS) {
    document.addEventListener(name, handleExternalCartEvent);
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted || sessionStorage.getItem(CAREGIVER_SWITCH_FLAG)) {
      handleCaregiverSwitchLanding().catch((error) => {
        console.error('[cart-sync] Failed to sync cart after navigation', error);
      });
    }
  });
}

initCartSync();
